import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { protocolsAPI, onedriveAPI } from "../lib/api";
import { formatDateTime, getDirectionLabel, getDirectionColor } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
  ArrowLeft,
  Edit,
  Trash2,
  FileText,
  User,
  Calendar,
  Download,
  Paperclip,
  Link as LinkIcon,
  ExternalLink,
  Eye,
  FolderOpen,
  Image,
  File,
  Copy,
  Check,
  FileSpreadsheet,
  FileType,
  Presentation,
  Loader2,
  Cloud,
  Printer,
  CopyPlus,
} from "lucide-react";

export function ProtocolDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  
  const [protocol, setProtocol] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDelete, setShowDelete] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(null);
  const [message, setMessage] = useState(null);

  const showMessage = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  useEffect(() => {
    loadProtocol();
  }, [id]);

  const loadProtocol = async () => {
    try {
      const response = await protocolsAPI.getOne(id);
      setProtocol(response.data);
    } catch (error) {
      showMessage("Protocollo non trovato", "error");
      navigate("/protocols");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await protocolsAPI.delete(id);
      navigate("/protocols?deleted=true");
    } catch (error) {
      showMessage("Errore durante l'eliminazione", "error");
      setShowDelete(false);
    }
  };

  const downloadAttachment = async (attachment, index) => {
    // OneDrive attachment
    if (attachment.onedrive_item_id) {
      try {
        const res = await onedriveAPI.getAttachmentContent(id, index);
        const blob = res.data;
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = attachment.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch {
        showMessage("Errore nel download dell'allegato", "error");
      }
      return;
    }
    // Base64 fallback
    if (attachment.data) {
      const link = document.createElement("a");
      const mimeType = attachment.mime_type || "application/octet-stream";
      link.href = `data:${mimeType};base64,${attachment.data}`;
      link.download = attachment.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const [copiedPath, setCopiedPath] = useState(null);

  // Print protocol detail
  const handlePrint = () => {
    window.print();
  };

  // Duplicate protocol - navigate to new form with pre-filled data
  const handleDuplicate = () => {
    if (!protocol) return;
    // Store protocol data in sessionStorage for duplication
    const duplicateData = {
      direction: protocol.direction,
      document_type: protocol.document_type,
      subject: protocol.subject,
      sender: protocol.sender || "",
      recipient: protocol.recipient || "",
      notes: protocol.notes || "",
      contact_id: protocol.contact_id || "",
      contact_category: protocol.contact_category || "",
      location_id: protocol.location_id || "",
      assigned_to: protocol.assigned_to || "",
    };
    sessionStorage.setItem("duplicateProtocol", JSON.stringify(duplicateData));
    navigate("/protocols/new?duplicate=true");
  };

  const copyFilePath = async (filePath) => {
    try {
      await navigator.clipboard.writeText(filePath);
      setCopiedPath(filePath);
      setTimeout(() => setCopiedPath(null), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = filePath;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopiedPath(filePath);
      setTimeout(() => setCopiedPath(null), 2000);
    }
  };

  const getFileIcon = (filename, mimeType) => {
    const fname = filename?.toLowerCase() || "";
    const mime = mimeType || "";
    
    // Images
    if (mime.startsWith("image/") || ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'].some(ext => fname.endsWith(ext))) {
      return Image;
    }
    
    // PDF
    if (mime === "application/pdf" || fname.endsWith('.pdf')) {
      return FileText;
    }
    
    // Word
    if (fname.endsWith('.doc') || fname.endsWith('.docx') || 
        mime.includes('msword') || mime.includes('wordprocessingml')) {
      return FileType;
    }
    
    // Excel
    if (fname.endsWith('.xls') || fname.endsWith('.xlsx') || 
        mime.includes('excel') || mime.includes('spreadsheetml')) {
      return FileSpreadsheet;
    }
    
    // PowerPoint
    if (fname.endsWith('.ppt') || fname.endsWith('.pptx') || 
        mime.includes('powerpoint') || mime.includes('presentationml')) {
      return Presentation;
    }
    
    return File;
  };

  const canPreview = (attachment) => {
    const mime = attachment.mime_type || attachment.content_type || "";
    const filename = attachment.filename?.toLowerCase() || "";
    
    // Check by MIME type
    if (mime.startsWith("image/") || mime === "application/pdf") {
      return true;
    }
    
    // Check by extension for common image formats
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
    if (imageExtensions.some(ext => filename.endsWith(ext))) {
      return true;
    }
    
    // Check file extension for PDF
    if (filename.endsWith('.pdf')) {
      return true;
    }
    
    return false;
  };

  const getMimeType = (attachment) => {
    const mime = attachment.mime_type || attachment.content_type || "";
    if (mime) return mime;
    
    // Guess MIME type from extension
    const filename = attachment.filename?.toLowerCase() || "";
    if (filename.endsWith('.jpg') || filename.endsWith('.jpeg')) return 'image/jpeg';
    if (filename.endsWith('.png')) return 'image/png';
    if (filename.endsWith('.gif')) return 'image/gif';
    if (filename.endsWith('.webp')) return 'image/webp';
    if (filename.endsWith('.svg')) return 'image/svg+xml';
    if (filename.endsWith('.pdf')) return 'application/pdf';
    
    return mime;
  };

  const [blobUrls, setBlobUrls] = useState({});

  const loadPreview = async (index) => {
    const attachment = protocol.attachments[index];
    if (blobUrls[index]) return; // Already loaded

    if (attachment.onedrive_item_id) {
      try {
        const res = await onedriveAPI.getAttachmentContent(id, index);
        const url = URL.createObjectURL(res.data);
        setBlobUrls((prev) => ({ ...prev, [index]: url }));
      } catch {
        showMessage("Errore nel caricamento dell'anteprima", "error");
      }
    }
  };

  // Clean up blob URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(blobUrls).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [blobUrls]);

  const renderPreview = (attachment, index) => {
    const mime = getMimeType(attachment);

    // OneDrive attachment: use blob URL
    if (attachment.onedrive_item_id) {
      const blobUrl = blobUrls[index];
      if (!blobUrl) {
        return (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-[#2D7BB7]" />
            <span className="ml-2 text-muted-foreground">Caricamento anteprima...</span>
          </div>
        );
      }
      if (mime.startsWith("image/")) {
        return (
          <img
            src={blobUrl}
            alt={attachment.filename}
            className="max-w-full max-h-[400px] object-contain rounded-lg border border-border"
          />
        );
      }
      if (mime === "application/pdf") {
        return (
          <iframe
            src={blobUrl}
            title={attachment.filename}
            className="w-full h-[500px] rounded-lg border border-border"
          />
        );
      }
      return <p className="text-muted-foreground">Anteprima non disponibile</p>;
    }

    // Base64 fallback
    if (!attachment.data) return null;

    const dataUrl = `data:${mime};base64,${attachment.data}`;
    
    if (mime.startsWith("image/")) {
      return (
        <img 
          src={dataUrl} 
          alt={attachment.filename}
          className="max-w-full max-h-[400px] object-contain rounded-lg border border-border"
        />
      );
    }
    
    if (mime === "application/pdf") {
      return (
        <iframe 
          src={dataUrl}
          title={attachment.filename}
          className="w-full h-[500px] rounded-lg border border-border"
        />
      );
    }
    
    return <p className="text-muted-foreground">Anteprima non disponibile</p>;
  };

  const getFileTypeInfo = (attachment) => {
    const filename = attachment.filename?.toLowerCase() || "";
    const mime = attachment.mime_type || attachment.content_type || "";
    
    // Word documents
    if (filename.endsWith('.doc') || filename.endsWith('.docx') || 
        mime.includes('msword') || mime.includes('wordprocessingml')) {
      return { type: 'word', label: 'Word', canPreview: false };
    }
    
    // Excel documents
    if (filename.endsWith('.xls') || filename.endsWith('.xlsx') || 
        mime.includes('excel') || mime.includes('spreadsheetml')) {
      return { type: 'excel', label: 'Excel', canPreview: false };
    }
    
    // PowerPoint
    if (filename.endsWith('.ppt') || filename.endsWith('.pptx') || 
        mime.includes('powerpoint') || mime.includes('presentationml')) {
      return { type: 'powerpoint', label: 'PowerPoint', canPreview: false };
    }
    
    return { type: 'other', label: null, canPreview: canPreview(attachment) };
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <Skeleton className="h-10 w-48 bg-muted" />
        <Skeleton className="h-64 w-full bg-muted rounded-xl" />
      </div>
    );
  }

  if (!protocol) {
    return null;
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in">
      {/* Message Banner */}
      {message && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
          message.type === "error" 
            ? "bg-red-500 text-white" 
            : "bg-emerald-500 text-white"
        }`}>
          {message.text}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="fixed inset-0 bg-black/50" 
            onClick={() => setShowDelete(false)}
          />
          <div className="relative z-50 bg-card border border-border rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Conferma eliminazione
            </h3>
            <p className="text-muted-foreground mb-6">
              Sei sicuro di voler eliminare il protocollo {protocol.protocol_number}? 
              Questa azione non puo essere annullata.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDelete(false)}
                className="px-4 py-2 border border-border rounded-md text-foreground hover:bg-muted transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                data-testid="confirm-delete"
              >
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-muted-foreground hover:text-foreground"
            data-testid="back-btn"
          >
            <ArrowLeft size={24} />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-heading font-bold text-foreground font-mono">
                {protocol.protocol_number}
              </h1>
              <Badge className={`${getDirectionColor(protocol.direction)} border`}>
                {getDirectionLabel(protocol.direction)}
              </Badge>
            </div>
            <p className="text-muted-foreground mt-1">{protocol.document_type}</p>
          </div>
        </div>
        
        {/* Action buttons - Print and Duplicate for everyone */}
        <div className="flex items-center gap-2 print:hidden">
          <Button
            variant="outline"
            onClick={handlePrint}
            className="border-border text-foreground hover:bg-muted"
            data-testid="print-btn"
          >
            <Printer className="mr-2" size={16} />
            Stampa
          </Button>
          <Button
            variant="outline"
            onClick={handleDuplicate}
            className="border-border text-foreground hover:bg-muted"
            data-testid="duplicate-btn"
          >
            <CopyPlus className="mr-2" size={16} />
            Duplica
          </Button>
          {isAdmin() && (
            <>
              <Link to={`/protocols/${id}/edit`}>
                <Button
                  variant="outline"
                  className="border-border text-foreground hover:bg-muted"
                  data-testid="edit-btn"
                >
                  <Edit className="mr-2" size={16} />
                  Modifica
                </Button>
              </Link>
              <Button
                variant="outline"
                onClick={() => setShowDelete(true)}
                className="border-red-200 text-red-600 hover:bg-red-50"
                data-testid="delete-btn"
              >
                <Trash2 className="mr-2" size={16} />
                Elimina
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Related Protocol */}
      {protocol.related_protocol_id && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <LinkIcon size={20} className="text-[#2D7BB7]" />
              <span className="text-foreground">Collegato a:</span>
              <Link
                to={`/protocols/${protocol.related_protocol_id}`}
                className="flex items-center gap-2 text-[#2D7BB7] font-mono font-medium hover:underline"
              >
                {protocol.related_protocol_number}
                <ExternalLink size={14} />
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Protocol Details */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
            <FileText size={20} className="text-[#2D7BB7]" />
            Dettagli Protocollo
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Subject */}
          <div>
            <label className="text-sm text-muted-foreground block mb-2">Oggetto</label>
            <p className="text-foreground text-lg" data-testid="protocol-subject">
              {protocol.subject}
            </p>
          </div>

          {/* Contact / Sender / Recipient */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {protocol.contact_name ? (
              <div>
                <label className="text-sm text-muted-foreground block mb-2">
                  {protocol.direction === "entrata" ? "Mittente (Anagrafica)" : "Destinatario (Anagrafica)"}
                </label>
                <p className="text-foreground font-medium" data-testid="protocol-contact-name">
                  {protocol.contact_name}
                </p>
              </div>
            ) : (
              <>
                <div>
                  <label className="text-sm text-muted-foreground block mb-2">Mittente</label>
                  <p className="text-foreground" data-testid="protocol-sender">
                    {protocol.sender || "-"}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground block mb-2">Destinatario</label>
                  <p className="text-foreground" data-testid="protocol-recipient">
                    {protocol.recipient || "-"}
                  </p>
                </div>
              </>
            )}
            {protocol.assigned_to_name && (
              <div>
                <label className="text-sm text-muted-foreground block mb-2">Assegnato a</label>
                <p className="text-foreground" data-testid="protocol-assigned-to">
                  {protocol.assigned_to_name}
                </p>
              </div>
            )}
          </div>

          {/* Location */}
          {protocol.location_name && (
            <div>
              <label className="text-sm text-muted-foreground block mb-2">Ubicazione Documento</label>
              <p className="text-foreground" data-testid="protocol-location">
                {protocol.location_name}
              </p>
            </div>
          )}

          {/* Notes */}
          {protocol.notes && (
            <div>
              <label className="text-sm text-muted-foreground block mb-2">Note</label>
              <p className="text-foreground whitespace-pre-wrap" data-testid="protocol-notes">
                {protocol.notes}
              </p>
            </div>
          )}

          {/* Metadata */}
          <div className="pt-4 border-t border-border">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 text-muted-foreground">
                <User size={18} />
                <span>Registrato da: <span className="text-foreground">{protocol.created_by_name}</span></span>
              </div>
              <div className="flex items-center gap-3 text-muted-foreground">
                <Calendar size={18} />
                <span>Data Registrazione: <span className="text-foreground">{protocol.registration_date || formatDateTime(protocol.created_at)}</span></span>
              </div>
              {protocol.document_date && (
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Calendar size={18} />
                  <span>Data Documento: <span className="text-foreground">{protocol.document_date}</span></span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* OneDrive Folder - show even without attachments */}
      {protocol.onedrive_folder && (!protocol.attachments || protocol.attachments.length === 0) && (
        <Card className="bg-card border-border">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Cloud size={16} className="text-sky-500" />
              <span>Cartella OneDrive: <span className="font-medium text-foreground">{protocol.onedrive_folder}</span></span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Attachments */}
      {protocol.attachments && protocol.attachments.length > 0 && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
              <Paperclip size={20} className="text-[#2D7BB7]" />
              Allegati ({protocol.attachments.length})
            </CardTitle>
            {protocol.onedrive_folder && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                <Cloud size={14} className="text-sky-500" />
                <span>Cartella: <span className="font-medium text-foreground">{protocol.onedrive_folder}</span></span>
              </div>
            )}
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {protocol.attachments.map((attachment, index) => {
                const FileIcon = getFileIcon(attachment.filename, attachment.mime_type);
                return (
                  <div
                    key={index}
                    className="p-4 bg-muted/50 rounded-lg border border-border"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <FileIcon size={24} className={`${
                          getFileTypeInfo(attachment).type === 'word' ? 'text-blue-600' :
                          getFileTypeInfo(attachment).type === 'excel' ? 'text-emerald-600' :
                          getFileTypeInfo(attachment).type === 'powerpoint' ? 'text-orange-600' :
                          'text-[#2D7BB7]'
                        }`} />
                        <div>
                          <p className="text-foreground font-medium">{attachment.filename}</p>
                          <div className="flex items-center gap-2">
                            {attachment.size && (
                              <span className="text-sm text-muted-foreground">
                                {(attachment.size / 1024).toFixed(1)} KB
                              </span>
                            )}
                            {getFileTypeInfo(attachment).label && (
                              <Badge variant="outline" className={`text-xs ${
                                getFileTypeInfo(attachment).type === 'word' ? 'border-blue-300 text-blue-700 bg-blue-50' :
                                getFileTypeInfo(attachment).type === 'excel' ? 'border-emerald-300 text-emerald-700 bg-emerald-50' :
                                getFileTypeInfo(attachment).type === 'powerpoint' ? 'border-orange-300 text-orange-700 bg-orange-50' :
                                ''
                              }`}>
                                {getFileTypeInfo(attachment).label}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {attachment.onedrive_item_id && (
                          <Badge variant="outline" className="text-xs border-sky-300 text-sky-700 bg-sky-50">
                            <Cloud size={12} className="mr-1" />
                            {attachment.onedrive_path ? attachment.onedrive_path : "OneDrive"}
                          </Badge>
                        )}
                        {canPreview(attachment) ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const newIdx = previewIndex === index ? null : index;
                              setPreviewIndex(newIdx);
                              if (newIdx !== null && attachment.onedrive_item_id) {
                                loadPreview(index);
                              }
                            }}
                            className="border-border text-foreground"
                            data-testid={`preview-attachment-${index}`}
                          >
                            <Eye size={16} className="mr-2" />
                            {previewIndex === index ? "Nascondi" : "Anteprima"}
                          </Button>
                        ) : getFileTypeInfo(attachment).label && (
                          <span className="text-xs text-muted-foreground px-2">
                            Scarica per visualizzare
                          </span>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadAttachment(attachment, index)}
                          className="border-border text-foreground"
                          data-testid={`download-attachment-${index}`}
                        >
                          <Download size={16} className="mr-2" />
                          Scarica
                        </Button>
                      </div>
                    </div>
                    
                    {/* File Path */}
                    {attachment.file_path && (
                      <div className="flex items-center gap-2 mb-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <FolderOpen size={16} className="text-amber-600 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-mono text-foreground truncate" title={attachment.file_path}>
                            {attachment.file_path}
                          </p>
                          <p className="text-xs text-amber-700 mt-1">
                            Percorso locale - Copia e incolla in Esplora Risorse
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyFilePath(attachment.file_path)}
                          className={`flex-shrink-0 ${copiedPath === attachment.file_path ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700 hover:bg-amber-100"}`}
                          data-testid={`copy-path-${index}`}
                        >
                          {copiedPath === attachment.file_path ? (
                            <>
                              <Check size={14} className="mr-1" />
                              Copiato!
                            </>
                          ) : (
                            <>
                              <Copy size={14} className="mr-1" />
                              Copia
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                    
                    {/* Preview */}
                    {previewIndex === index && (
                      <div className="mt-3 p-3 bg-background rounded-lg border border-border">
                        {renderPreview(attachment, index)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
